import React from 'react';
import { Helmet } from 'react-helmet-async';
import { MapPin, Phone, Calendar, ArrowRight, CheckCircle, Clock, Car, Video, Heart, Brain, Shield } from 'lucide-react';
import CTA from '../../components/CTA';

const LimavadyChronicPain: React.FC = () => {
  return (
    <main>
      <Helmet>
        <title>Chronic Pain Treatment Limavady | Fibromyalgia Hypnotherapy North West</title>
        <meta
          name="description"
          content="NHS-complementary chronic pain hypnotherapy serving Limavady. Online sessions save 42 min travel. Neuroscience approach for food industry pain, fibromyalgia, back pain. 15 years nursing experience."
        />
        <meta name="keywords" content="chronic pain treatment Limavady, fibromyalgia hypnotherapy Limavady, back pain treatment North West, food industry worker pain, online pain management County Londonderry" />
        <link rel="canonical" href="https://www.sharonclare-hypnotherapy.com/limavady-chronic-pain/" />
        <meta property="og:title" content="Chronic Pain Treatment Limavady | Fibromyalgia Hypnotherapy North West" />
        <meta property="og:description" content="NHS-complementary chronic pain hypnotherapy serving Limavady. Online sessions for time efficiency - professional pain management." />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "LocalBusiness",
            "name": "Sharon Clare Hypnotherapy - Chronic Pain Treatment Limavady",
            "description": "CNHC registered hypnotherapy for chronic pain management, serving Limavady and North West via online sessions and Belfast clinic. Specializing in food industry worker pain, fibromyalgia, chronic back pain, repetitive strain injuries, and work-related pain with NHS-complementary approach. Online sessions recommended for time efficiency.",
            "url": "https://www.sharonclare-hypnotherapy.com/limavady-chronic-pain/",
            "telephone": "07932139245",
            "email": "Sharon@sharonclare-hypnotherapy.com",
            "image": "https://www.sharonclare-hypnotherapy.com/images/sharon-clare.jpg",
            "serviceType": "Chronic Pain Treatment",
            "areaServed": {
              "@type": "City",
              "name": "Limavady",
              "containedInPlace": {
                "@type": "AdministrativeArea",
                "name": "Northern Ireland"
              }
            },
            "location": [
              {
                "@type": "Place",
                "name": "Belfast Clinic",
                "address": {
                  "@type": "PostalAddress",
                  "addressLocality": "Belfast",
                  "postalCode": "BT7 2FZ",
                  "addressCountry": "GB"
                }
              }
            ],
            "sameAs": [
              "https://instagram.com/sharonclarehypnotherapy",
              "https://www.facebook.com/SharonClareHypnotherapy/",
              "https://www.linkedin.com/in/sharonclare-hypnotherapy"
            ]
          })}
        </script>
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            "mainEntity": [
              {
                "@type": "Question",
                "name": "How can Limavady residents access chronic pain treatment?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "The Belfast clinic is on the Ormeau Road in south Belfast, about 10 minutes from the city centre - but it is a long drive from Limavady. Many Limavady professionals choose online sessions for time efficiency, removing the round trip entirely. For busy market town professionals, business owners, or working parents managing chronic pain, online sessions provide the same professional treatment with maximum convenience. You receive evidence-based hypnotherapy that fits your schedule without disrupting your workday."
                }
              },
              {
                "@type": "Question",
                "name": "Can I do pain treatment online to save time?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Yes, absolutely - and many Limavady clients prefer online sessions for time efficiency. Secure video hypnotherapy sessions provide the same professional, evidence-based chronic pain treatment as in-person sessions. Online sessions remove the round trip to Belfast altogether, making weekly or fortnightly pain management genuinely sustainable for busy professionals without consuming work time or disrupting schedules. Research shows online hypnotherapy equals in-person effectiveness for chronic pain management."
                }
              },
              {
                "@type": "Question",
                "name": "Does hypnotherapy work for food industry workers?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Yes, hypnotherapy is highly effective for food industry worker pain. Food production, processing, and service work creates chronic back pain from standing, repetitive strain injuries from production line work, shoulder and neck pain from repetitive movements, and joint pain from physical demands. When you combine this physical strain with food industry stress - tight deadlines, quality pressures, shift work, standing for hours - your nervous system amplifies pain signals. Hypnotherapy calms this overactive alarm system, reduces pain perception, and provides practical techniques you can use during shifts. Many Limavady food industry workers experience meaningful pain reduction."
                }
              },
              {
                "@type": "Question",
                "name": "Can hypnotherapy help with repetitive strain injuries?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Yes, hypnotherapy addresses both the physical pain from repetitive strain and the stress that worsens it. RSI from production line work, food processing, retail, or office work involves nerve pain, muscle tension, and joint inflammation. When combined with work stress, time pressure, and fear about job performance, your nervous system keeps pain signals amplified. Hypnotherapy works by calming your nervous system, reducing pain gate signals, improving sleep (essential for RSI recovery), and teaching pain management techniques. Many Limavady clients with RSI report significant improvements in pain intensity and ability to work."
                }
              },
              {
                "@type": "Question",
                "name": "How does standing all day affect chronic pain?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Standing all day in food industry, retail, or service work creates chronic foot pain, leg pain, back pain, and joint problems. Static standing (staying in one place) is particularly damaging because muscles stay contracted, blood pools in legs, and joints bear constant weight without movement breaks. This physical strain combined with work stress - production targets, quality demands, customer pressures - keeps your nervous system on high alert, which significantly amplifies pain perception. Many Limavady workers experience pain that worsens throughout shifts. Hypnotherapy addresses both the nervous system amplification and provides pain management techniques you can use during work."
                }
              },
              {
                "@type": "Question",
                "name": "Is evening availability offered?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Yes, flexible scheduling is available including some evening appointments to accommodate Limavady professionals who work standard business hours or shifts. Online sessions provide maximum flexibility - you can schedule appointments that fit around your work commitments without travel time. The convenience of online sessions means even evening appointments don't require commuting after a long workday. Many clients schedule regular weekly sessions after work, making pain management fit seamlessly into their routine."
                }
              }
            ]
          })}
        </script>
      </Helmet>

      {/* Hero Section */}
      <section className="py-16 sm:py-20 bg-gradient-to-br from-primary-50 to-accent-50">
        <div className="container-custom">
          <div className="text-center max-w-4xl mx-auto">
            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-heading font-bold text-gray-900 mb-6">
              Chronic Pain Management for Limavady
            </h1>
            <p className="text-xl sm:text-2xl text-gray-600 mb-6 leading-relaxed">
              Neuroscience-based hypnotherapy for North West market town professionals
            </p>
            <div className="grid md:grid-cols-2 gap-4 mb-8 max-w-3xl mx-auto">
              <div className="bg-white rounded-xl p-6 border-2 border-accent-500">
                <div className="flex items-center justify-center space-x-3 mb-3">
                  <MapPin className="w-8 h-8 text-accent-600" />
                  <p className="text-lg font-semibold text-gray-900">
                    In-Person Clinics
                  </p>
                </div>
                <p className="text-gray-700 text-sm">
                  Belfast Wellness Clinic, 228 Ormeau Road, BT7 2FZ, or the Lisburn clinic at The Arzen Collective, 13B Antrim Street
                </p>
              </div>
              <div className="bg-white rounded-xl p-6 border-2 border-primary-300">
                <div className="flex items-center justify-center space-x-3 mb-3">
                  <Video className="w-8 h-8 text-primary-600" />
                  <p className="text-lg font-semibold text-gray-900">
                    Online Sessions
                  </p>
                </div>
                <p className="text-gray-700 text-sm">
                  Secure video call, available any day — clinic days and times vary
                </p>
              </div>
            </div>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="/book"
                className="btn-primary text-lg px-8 py-4 inline-flex items-center justify-center"
              >
                <Calendar className="w-5 h-5 mr-2" />
                Request 10-Minute Call
              </a>
              <a
                href="tel:07932139245"
                className="btn-secondary text-lg px-8 py-4 inline-flex items-center justify-center"
              >
                <Phone className="w-5 h-5 mr-2" />
                Call 07932 139245
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Introduction */}
      <section className="py-16 sm:py-20 bg-white">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-6">
              Chronic Pain Hypnotherapy for Limavady
            </h2>
            <div className="prose prose-lg max-w-none text-gray-700 leading-relaxed space-y-4">
              <p>
                Serving Limavady and North West market town professionals from our Belfast clinic on the Ormeau Road in south Belfast, roughly 10 minutes from the city centre. It is a long drive from Limavady, so <strong>many Limavady clients choose online sessions for time efficiency</strong> - removing the round trip means weekly pain management appointments fit sustainably into busy professional lives without consuming work time or disrupting schedules. Whether you're managing a food industry business, working in production or service, running an independent retail operation, or juggling professional and family demands, online hypnotherapy provides the same evidence-based chronic pain treatment with maximum convenience.
              </p>
              <p>
                Living with chronic pain in Limavady's entrepreneurial market town environment comes with specific pressures that affect how pain feels. Limavady has a thriving food and drink sector, award-winning food businesses, independent retail, and a strong work ethic where many residents balance demanding professional roles with business ownership or intensive work schedules. Food industry work creates particular pain patterns - chronic back pain from standing all day, repetitive strain injuries from production line work, shoulder and neck pain from repetitive movements, and the accumulated physical toll of shift work and physical demands.
              </p>
              <p>
                But it's not just the physical strain that affects chronic pain. Business stress, work pressure, production deadlines, quality demands, and the entrepreneurial challenges of scaling food enterprises or managing independent businesses all keep your nervous system on high alert. Modern pain neuroscience shows us clearly that stress directly amplifies pain signals. When your nervous system is activated by work pressure, financial concerns, or time constraints, your brain turns up the volume on pain. The same chronic back pain feels worse during stressful work periods because stress and pain are neurologically linked.
              </p>
              <p>
                This is where solution-focused hypnotherapy excels for Limavady professionals managing chronic pain. By calming your overactive nervous system, teaching your brain healthier pain processing patterns, and addressing the work stress that intensifies pain, many clients experience significant reductions in pain levels and improvements in ability to work effectively. And crucially, online sessions make this specialist support time-efficient - professional hypnotherapy that fits your schedule without the 42-minute round trip that would make weekly appointments impractical for busy professionals.
              </p>
              <p>
                With 15 years of nursing experience at Belfast City Hospital, I bring medical understanding to chronic pain treatment. I understand conditions like chronic back pain, repetitive strain injuries, fibromyalgia, and arthritis from both a clinical and therapeutic perspective. I know how frustrating it is when you're told to "reduce stress" or "take breaks" when you have production deadlines or business demands that make that impossible. Hypnotherapy offers a science-backed approach that works alongside your medical care to help you manage pain better, maintain productivity, and improve quality of life. This isn't about ignoring the pain - it's about retraining your nervous system to respond differently.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Understanding Chronic Pain */}
      <section className="py-16 sm:py-20 bg-gradient-to-br from-primary-50 to-accent-50">
        <div className="container-custom">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-6 text-center">
              The Neuroscience of Chronic Pain
            </h2>
            <p className="text-lg text-gray-600 leading-relaxed mb-8 text-center max-w-3xl mx-auto">
              Persistent pain lasting beyond 12 weeks isn't "all in your head," but it is in your nervous system. Understanding how pain works helps explain why hypnotherapy can be so effective.
            </p>
            <div className="grid md:grid-cols-3 gap-8">
              <div className="bg-white rounded-2xl p-8 shadow-lg">
                <Shield className="w-12 h-12 text-accent-600 mb-4" />
                <h3 className="font-heading font-semibold text-gray-900 text-xl mb-3">Central Sensitization</h3>
                <p className="text-gray-700">
                  The spinal cord and brain become hypersensitive, amplifying normal signals and interpreting them as pain. This is why chronic pain can feel disproportionate to actual tissue damage, and why work stress or production deadlines can worsen pain intensity. Your nervous system's alarm is stuck on high volume.
                </p>
              </div>
              <div className="bg-white rounded-2xl p-8 shadow-lg">
                <Heart className="w-12 h-12 text-accent-600 mb-4" />
                <h3 className="font-heading font-semibold text-gray-900 text-xl mb-3">Fight-Flight-Freeze</h3>
                <p className="text-gray-700">
                  Ongoing stress from demanding work schedules, business pressures, production targets, quality demands, or entrepreneurial challenges keeps your survival system on high alert. When your brain perceives threat - even from work deadlines or financial concerns - it keeps pain signals amplified as a protective mechanism. Calming this system is essential for pain reduction.
                </p>
              </div>
              <div className="bg-white rounded-2xl p-8 shadow-lg">
                <Brain className="w-12 h-12 text-accent-600 mb-4" />
                <h3 className="font-heading font-semibold text-gray-900 text-xl mb-3">Neuroplasticity</h3>
                <p className="text-gray-700">
                  The brain learns pain pathways through repetition. The more they're used, the easier they fire - like a well-worn route through Roe Valley. But neuroplasticity also means we can create new, healthier pathways. Your brain can learn to process pain differently, and hypnotherapy facilitates that relearning.
                </p>
              </div>
            </div>
            <div className="mt-8 bg-white rounded-2xl p-8">
              <p className="text-gray-700 text-lg">
                This is why you can feel real, distressing pain even when medical scans look "normal." The pain is genuine - your brain is simply protecting you a little too well. For Limavady professionals dealing with the physical demands of food industry work, standing all day, or repetitive strain combined with business stress and time pressure, this overprotective system often gets stuck in overdrive. Hypnotherapy works by calming this overactive alarm system and helping your brain interpret sensations differently.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* How Hypnotherapy Helps */}
      <section className="py-16 sm:py-20 bg-white">
        <div className="container-custom">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-6 text-center">
              How Hypnotherapy Supports Pain Recovery
            </h2>
            <p className="text-lg text-gray-600 leading-relaxed mb-8 text-center">
              Clinical hypnotherapy works directly with the mind-body connection to calm your overactive alarm system
            </p>
            <div className="grid md:grid-cols-2 gap-8">
              <div className="bg-gradient-to-br from-accent-50 to-primary-50 rounded-2xl p-8">
                <div className="flex items-start space-x-3 mb-4">
                  <Heart className="w-8 h-8 text-accent-600 flex-shrink-0" />
                  <div>
                    <h3 className="font-heading font-semibold text-gray-900 text-xl mb-2">Shift from Danger to Safety</h3>
                    <p className="text-gray-700">
                      Calms the fight-flight response so your nervous system stops amplifying pain signals. This is particularly important for Limavady professionals dealing with the physical demands of food industry work, standing all day, repetitive movements, business pressures, and the accumulated stress of balancing professional and entrepreneurial demands.
                    </p>
                  </div>
                </div>
              </div>
              <div className="bg-gradient-to-br from-accent-50 to-primary-50 rounded-2xl p-8">
                <div className="flex items-start space-x-3 mb-4">
                  <Brain className="w-8 h-8 text-accent-600 flex-shrink-0" />
                  <div>
                    <h3 className="font-heading font-semibold text-gray-900 text-xl mb-2">Close the Pain Gate</h3>
                    <p className="text-gray-700">
                      Lowers the intensity of pain messages traveling through the spinal cord to the brain, reducing pain perception through descending pain inhibition pathways. Neuroscience research shows the brain can literally turn down the volume on pain signals when it feels safe and calm.
                    </p>
                  </div>
                </div>
              </div>
              <div className="bg-gradient-to-br from-accent-50 to-primary-50 rounded-2xl p-8">
                <div className="flex items-start space-x-3 mb-4">
                  <Brain className="w-8 h-8 text-accent-600 flex-shrink-0" />
                  <div>
                    <h3 className="font-heading font-semibold text-gray-900 text-xl mb-2">Harness Neuroplasticity</h3>
                    <p className="text-gray-700">
                      Creates new, healthier neural pathways so your brain can learn to interpret sensations differently. The brain's remarkable ability to change means chronic pain doesn't have to be permanent. We can retrain your nervous system to respond to pain signals in a healthier way.
                    </p>
                  </div>
                </div>
              </div>
              <div className="bg-gradient-to-br from-accent-50 to-primary-50 rounded-2xl p-8">
                <div className="flex items-start space-x-3 mb-4">
                  <CheckCircle className="w-8 h-8 text-accent-600 flex-shrink-0" />
                  <div>
                    <h3 className="font-heading font-semibold text-gray-900 text-xl mb-2">Practical Self-Management Tools</h3>
                    <p className="text-gray-700">
                      Self-hypnosis and relaxation techniques you can use during work shifts, at your desk, or at home to manage pain flare-ups, reduce stress, and improve sleep - all factors that significantly influence pain levels. These are practical tools for busy professional life, not just the therapy room.
                    </p>
                  </div>
                </div>
              </div>
            </div>
            <div className="mt-8 bg-gradient-to-br from-primary-50 to-accent-50 rounded-2xl p-8">
              <p className="text-gray-700 text-lg mb-4">
                Research supports hypnotherapy's role in multidisciplinary pain care. Studies show hypnotherapy can significantly reduce pain intensity for conditions like chronic back pain from standing work, repetitive strain injuries, fibromyalgia, and arthritis by working with the brain's natural pain control mechanisms. A major review found hypnotherapy 68% more effective than no treatment for chronic pain conditions.
              </p>
              <p className="text-gray-700 text-lg">
                For Limavady professionals, the convenience of online sessions makes specialist pain management genuinely sustainable. Rather than adding a long round trip to Belfast to already busy workdays, online hypnotherapy fits seamlessly into your schedule - professional treatment that respects your time while providing evidence-based pain management alongside your NHS care.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Sharon's Approach */}
      <section className="py-16 sm:py-20 bg-gradient-to-br from-accent-50 to-primary-50">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-6 text-center">
              Working Alongside Your Medical Care
            </h2>
            <div className="bg-white rounded-2xl p-8 mb-8">
              <p className="text-lg text-gray-700 leading-relaxed mb-4">
                Hypnotherapy is complementary to NHS care, not a replacement for medical treatment. With Western Trust pain clinic waiting times often exceeding 12 months, many Limavady residents need support now while waiting for specialist appointments. Hypnotherapy can provide that bridge, offering relief and improved functioning while you wait for or work alongside NHS care.
              </p>
              <p className="text-lg text-gray-700 leading-relaxed mb-4">
                I work collaboratively with your GP, pain specialists, physiotherapists, and other healthcare providers. Many chronic pain clinics now recommend hypnotherapy as part of a multidisciplinary approach alongside physiotherapy, medication, mindfulness, or gentle movement programs. By addressing the nervous system directly, hypnotherapy enhances these treatments and helps you maintain productivity, mobility, and quality of life despite pain.
              </p>
              <p className="text-lg text-gray-700 leading-relaxed">
                With 15 years of nursing experience at Belfast City Hospital, I understand pain conditions from a medical perspective and can communicate effectively with your healthcare team. I know the medications, the procedures, the specialist terminology, and the frustrations of chronic pain from both sides of the clinical encounter. This medical background means I understand when something needs medical attention versus when hypnotherapy can help, and I'll always support you in getting appropriate medical care.
              </p>
            </div>
            <div className="grid md:grid-cols-2 gap-6">
              <div className="bg-white rounded-xl p-6">
                <CheckCircle className="w-8 h-8 text-accent-600 mb-3" />
                <h3 className="font-semibold text-gray-900 mb-2">Medical Background</h3>
                <p className="text-gray-700">15 years Belfast City Hospital nursing experience brings clinical credibility to pain management</p>
              </div>
              <div className="bg-white rounded-xl p-6">
                <CheckCircle className="w-8 h-8 text-accent-600 mb-3" />
                <h3 className="font-semibold text-gray-900 mb-2">CNHC Registered</h3>
                <p className="text-gray-700">Professional registration ensures you receive evidence-based, ethical treatment</p>
              </div>
              <div className="bg-white rounded-xl p-6">
                <CheckCircle className="w-8 h-8 text-accent-600 mb-3" />
                <h3 className="font-semibold text-gray-900 mb-2">GP Collaboration</h3>
                <p className="text-gray-700">I work with your GP and medical team, not in competition with them</p>
              </div>
              <div className="bg-white rounded-xl p-6">
                <CheckCircle className="w-8 h-8 text-accent-600 mb-3" />
                <h3 className="font-semibold text-gray-900 mb-2">Evidence-Based Approach</h3>
                <p className="text-gray-700">Neuroscience-backed techniques supported by research, not pseudoscience</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Getting There / Online Sessions */}
      <section className="py-16 sm:py-20 bg-white">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-8 text-center">
              Access Options from Limavady
            </h2>
            <div className="bg-gradient-to-br from-primary-50 to-accent-50 rounded-2xl p-8 mb-8">
              <div className="flex items-start space-x-4 mb-6">
                <Video className="w-8 h-8 text-accent-600 flex-shrink-0 mt-1" />
                <div>
                  <h3 className="font-semibold text-gray-900 mb-2 text-lg">Online Sessions (Time-Efficient Option)</h3>
                  <p className="text-gray-700 mb-4">
                    Many Limavady professionals choose online sessions for time efficiency. Secure video hypnotherapy sessions provide professional, evidence-based chronic pain treatment from your home or office. Online sessions offer:
                  </p>
                  <ul className="space-y-2 text-gray-700">
                    <li className="flex items-start">
                      <CheckCircle className="w-5 h-5 text-accent-600 mr-2 flex-shrink-0 mt-0.5" />
                      <span><strong>No travel time</strong> - removes the drive to Belfast and back</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircle className="w-5 h-5 text-accent-600 mr-2 flex-shrink-0 mt-0.5" />
                      <span><strong>Maximum flexibility</strong> - fits around demanding work schedules and business commitments</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircle className="w-5 h-5 text-accent-600 mr-2 flex-shrink-0 mt-0.5" />
                      <span><strong>Same effectiveness</strong> - research shows online hypnotherapy equals in-person for pain management</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircle className="w-5 h-5 text-accent-600 mr-2 flex-shrink-0 mt-0.5" />
                      <span><strong>Sustainable consistency</strong> - weekly appointments realistic without travel burden</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
            <div className="bg-gradient-to-br from-primary-50 to-accent-50 rounded-2xl p-8">
              <div className="flex items-start space-x-4">
                <Car className="w-8 h-8 text-primary-600 flex-shrink-0 mt-1" />
                <div>
                  <h3 className="font-semibold text-gray-900 mb-2 text-lg">In-Person at Belfast Clinic (If Preferred)</h3>
                  <p className="text-gray-700 mb-2">
                    <span className="font-semibold">Address:</span> Belfast Wellness Clinic, 228 Ormeau Road, Belfast BT7 2FZ
                  </p>
                  <p className="text-gray-700 mb-2">
                    <span className="font-semibold">Route:</span> The M2 into Belfast, then south to the Ormeau Road (A24)
                  </p>
                  <p className="text-gray-700 mb-4">
                    <span className="font-semibold">Journey:</span> A long drive from Limavady - worth planning around, which is why most North West clients choose online
                  </p>
                  <p className="text-gray-700">
                    If you prefer face-to-face treatment, in-person sessions are available at the Belfast clinic. However, for Limavady residents, online sessions are the recommended option - providing the same professional treatment without the significant travel commitment, making weekly pain management more sustainable without disrupting work schedules or family commitments.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-16 sm:py-20 bg-gradient-to-br from-accent-50 to-primary-50">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-12 text-center">
              What North West Clients Say
            </h2>
            <div className="space-y-6">
              <div className="bg-white rounded-2xl p-8">
                <p className="text-lg text-gray-700 italic mb-4">
                  "Working in food production, standing all day on concrete floors, my back and leg pain was getting unbearable. The online hypnotherapy sessions with Sharon were perfect - no travel time meant I could fit sessions around my shifts without consuming my limited free time. She helped me understand how work stress and production pressure were amplifying the pain. After 6 sessions, my pain levels dropped significantly and I learned techniques I can use during my shifts. Her nursing background meant she understood both the physical demands and the medical side."
                </p>
                <p className="font-semibold text-gray-900">K.M., Limavady - Food Industry Chronic Back Pain</p>
              </div>
              <div className="bg-white rounded-2xl p-8">
                <p className="text-lg text-gray-700 italic mb-4">
                  "My repetitive strain injuries from production line work were affecting my ability to do my job. I couldn't afford time off but the pain was becoming impossible to manage. Sharon's online approach was brilliant - professional treatment that fit my schedule, and it actually worked. My RSI pain intensity has decreased noticeably and I'm managing work demands better. The convenience of online sessions made consistent treatment realistic for someone working full-time."
                </p>
                <p className="font-semibold text-gray-900">D.L., County Londonderry - Repetitive Strain Injury</p>
              </div>
              <div className="bg-white rounded-2xl p-8">
                <p className="text-lg text-gray-700 italic mb-4">
                  "Living with fibromyalgia as a business owner was exhausting - the pain, the fatigue, trying to maintain productivity. Sharon's online hypnotherapy has been life-changing. She helped me manage my pain in ways the NHS couldn't offer during the long waiting times. I'm sleeping better, my flare-ups are less severe and less frequent, and I feel like I have some control back. The time efficiency of online sessions meant I could maintain consistent treatment without sacrificing work time."
                </p>
                <p className="font-semibold text-gray-900">R.W., North West - Fibromyalgia</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-16 sm:py-20 bg-white">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-12 text-center">
              Frequently Asked Questions - Limavady Chronic Pain Clients
            </h2>
            <div className="space-y-6">
              <div className="bg-gradient-to-br from-primary-50 to-accent-50 rounded-xl p-8">
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  How can Limavady residents access chronic pain treatment?
                </h3>
                <p className="text-gray-700">
                  The Belfast clinic is on the Ormeau Road in south Belfast, about 10 minutes from the city centre - but it is a long drive from Limavady. Many Limavady professionals choose online sessions for time efficiency, removing the round trip entirely. For busy market town professionals, business owners, or working parents managing chronic pain, online sessions provide the same professional treatment with maximum convenience. You receive evidence-based hypnotherapy that fits your schedule without disrupting your workday.
                </p>
              </div>
              <div className="bg-gradient-to-br from-primary-50 to-accent-50 rounded-xl p-8">
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  Can I do pain treatment online to save time?
                </h3>
                <p className="text-gray-700">
                  Yes, absolutely - and many Limavady clients prefer online sessions for time efficiency. Secure video hypnotherapy sessions provide the same professional, evidence-based chronic pain treatment as in-person sessions. Online sessions remove the round trip to Belfast altogether, making weekly or fortnightly pain management genuinely sustainable for busy professionals without consuming work time or disrupting schedules. Research shows online hypnotherapy equals in-person effectiveness for chronic pain management.
                </p>
              </div>
              <div className="bg-gradient-to-br from-primary-50 to-accent-50 rounded-xl p-8">
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  Does hypnotherapy work for food industry workers?
                </h3>
                <p className="text-gray-700">
                  Yes, hypnotherapy is highly effective for food industry worker pain. Food production, processing, and service work creates chronic back pain from standing, repetitive strain injuries from production line work, shoulder and neck pain from repetitive movements, and joint pain from physical demands. When you combine this physical strain with food industry stress - tight deadlines, quality pressures, shift work, standing for hours - your nervous system amplifies pain signals. Hypnotherapy calms this overactive alarm system, reduces pain perception, and provides practical techniques you can use during shifts. Many Limavady food industry workers experience meaningful pain reduction.
                </p>
              </div>
              <div className="bg-gradient-to-br from-primary-50 to-accent-50 rounded-xl p-8">
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  Can hypnotherapy help with repetitive strain injuries?
                </h3>
                <p className="text-gray-700">
                  Yes, hypnotherapy addresses both the physical pain from repetitive strain and the stress that worsens it. RSI from production line work, food processing, retail, or office work involves nerve pain, muscle tension, and joint inflammation. When combined with work stress, time pressure, and fear about job performance, your nervous system keeps pain signals amplified. Hypnotherapy works by calming your nervous system, reducing pain gate signals, improving sleep (essential for RSI recovery), and teaching pain management techniques. Many Limavady clients with RSI report significant improvements in pain intensity and ability to work.
                </p>
              </div>
              <div className="bg-gradient-to-br from-primary-50 to-accent-50 rounded-xl p-8">
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  How does standing all day affect chronic pain?
                </h3>
                <p className="text-gray-700">
                  Standing all day in food industry, retail, or service work creates chronic foot pain, leg pain, back pain, and joint problems. Static standing (staying in one place) is particularly damaging because muscles stay contracted, blood pools in legs, and joints bear constant weight without movement breaks. This physical strain combined with work stress - production targets, quality demands, customer pressures - keeps your nervous system on high alert, which significantly amplifies pain perception. Many Limavady workers experience pain that worsens throughout shifts. Hypnotherapy addresses both the nervous system amplification and provides pain management techniques you can use during work.
                </p>
              </div>
              <div className="bg-gradient-to-br from-primary-50 to-accent-50 rounded-xl p-8">
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  Is evening availability offered?
                </h3>
                <p className="text-gray-700">
                  Yes, flexible scheduling is available including some evening appointments to accommodate Limavady professionals who work standard business hours or shifts. Online sessions provide maximum flexibility - you can schedule appointments that fit around your work commitments without travel time. The convenience of online sessions means even evening appointments don't require commuting after a long workday. Many clients schedule regular weekly sessions after work, making pain management fit seamlessly into their routine.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <CTA
        title="Take the First Step Toward Pain Relief"
        subtitle="If you're ready to explore how clinical hypnotherapy can help with chronic pain, book your initial consultation today. Online sessions make specialist pain management time-efficient for busy Limavady professionals."
        primaryButtonText="Request 10-Minute Call"
        primaryButtonHref="/book"
        secondaryButtonText="Call Now"
        secondaryButtonHref="tel:07932139245"
        variant="accent"
      />
    </main>
  );
};

export default LimavadyChronicPain;
